import { captureException } from '@sentry/react';
import { useRef } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom-v5-compat';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { SAR, SARData } from 'dashboard/models/sar';
import { showMessage } from 'dashboard/redux/actions/messages';
import { useListUsersQuery } from 'dashboard/redux/api/users';
import { RootState } from 'dashboard/redux/store';
import {
  archiveSAR,
  autofillSAR,
  exportSAR,
  fileSAR,
  loadSAR,
  updateSAR,
  validateSAR,
} from 'dashboard/redux/thunk-actions/sar';
import { loadSARConfiguration } from 'dashboard/redux/thunk-actions/sar-configuration';
import DashboardSARShowView from 'dashboard/views/DashboardSARShowView';
import ApiResponseError from 'lib/errors/ApiResponseError';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useRedirectOnEnvironmentChange from 'lib/hooks/useRedirectOnEnvironmentChange';
import useRTKErrorHandler from 'lib/hooks/useRTKErrorHandler';
import { ApiModel } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

const nullStateMessages: Record<number, string> = {
  403: "You don't have permissions to view this SAR.",
  404: "We couldn't find that SAR.",
};

type Params = { sarId: string };

const DashboardSARShowRoute = (props: PropsFromRedux) => {
  const navigate = useNavigate();
  const { sarId } = useParams<Params>() as Params; // sarId will always be defined
  const errorCode = useRef<number>(null);
  const sarToken = sarId;

  useRedirectOnEnvironmentChange('/dashboard/sars');

  const { loading: isLoading, callback: loadSAR } = useApiLoader({
    loader: () => props.loadSAR(sarToken),
    deps: [props.loadSAR, sarToken],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load SAR.', MessageType.Failure);
      }

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      errorCode.current = error.status;
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { loading: loadingSARConfiguration } = useApiLoader({
    loader: () => props.loadSARConfiguration(props.organization.id),
    deps: [props.loadSARConfiguration, props.organization.id],
    onError: (error) => {
      if (isPermissionsError(error)) {
        // ignore permissions error for SAR configuration since non-critical and only used to show validations
      } else {
        props.showMessage('Failed to load SAR configuration.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { error: updateSARDataError, callback: handleUpdateSAR } = useApiCallback(
    async (sarData: SARData) => {
      await props.updateSAR(sarToken, { data: sarData });
      props.showMessage('Successfully updated SAR.', MessageType.Success, false);
    },
    [props.updateSAR, sarToken],
  );

  useApiErrorHandler({
    error: updateSARDataError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        captureException(error);
        props.showMessage('Failed to update SAR, please try again.', MessageType.Failure, false);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { error: exportSARError, callback: handleExportSAR } = useApiCallback(async () => {
    await props.exportSAR(sarToken);
    await loadSAR();
    props.showMessage('SAR exported successfully.', MessageType.Success);
  }, [props.sar, props.exportSAR]);

  useApiErrorHandler({
    error: exportSARError,
    onError: (error) => {
      if (error instanceof ApiResponseError && error.status === 403) {
        // [chris] todo: isPermissionsError never returns true for the export endpoint.
        // We manually generate the permissions error message.
        props.showMessage(
          "You don't have permissions for an action on this page: Export SAR",
          MessageType.Failure,
        );
      } else if (error instanceof ApiResponseError && error.status === 422) {
        props.showMessage(
          'Failed to export as XML, please make sure the form is valid before exporting.',
          MessageType.Failure,
        );
      } else {
        props.showMessage('Failed to export as XML, please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { error: fileSARError, callback: handleFileSAR } = useApiCallback(async () => {
    await props.fileSAR(sarToken);
    await loadSAR();
    props.showMessage('Successfully sent SAR to FinCEN.', MessageType.Success);
  }, [props.fileSAR, props.showMessage, sarToken]);

  useApiErrorHandler({
    error: fileSARError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else if (error instanceof ApiResponseError && error.status === 401) {
        props.showMessage(
          'Failed to file SAR, please provide valid FinCEN credentials.',
          MessageType.Failure,
        );
      } else if (error instanceof ApiResponseError && error.status === 422) {
        props.showMessage(
          'Failed to file SAR, please make sure the form is valid before filing.',
          MessageType.Failure,
        );
      } else {
        props.showMessage('Failed to file SAR, please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { error: archiveSARError, callback: handleArchiveSAR } = useApiCallback(async () => {
    await props.archiveSAR(sarToken);
    await loadSAR();
    props.showMessage('Successfully archived SAR.', MessageType.Success);
  }, [props.archiveSAR, props.showMessage, sarToken]);

  useApiErrorHandler({
    error: archiveSARError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to file SAR, please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { error: autofillSARError, callback: handleAutofillSAR } = useApiCallback(async () => {
    props.showMessage('Autofilling SAR with attached objects, please wait.', MessageType.Notice);
    await props.autofillSAR(sarToken);
    await loadSAR();
  }, [props.autofillSAR, sarToken]);

  useApiErrorHandler({
    error: autofillSARError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to autofill SAR with attached objects.');
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { error: cloneSARError, callback: handleCloneSAR } = useApiCallback(
    async ({ caseTokens }: { caseTokens: string[] }) => {
      const response = await SessionClientDashboard.postResourceAction<SAR>(
        {
          type: ApiModel.SAR,
          id: sarToken,
        },
        'clone',
        {
          meta: {
            case_tokens: caseTokens,
          },
        },
      );

      navigate(`/dashboard/sars/${response.data.id}`);
      props.showMessage('Successfully cloned SAR.', MessageType.Success);
    },
    [sarToken],
  );

  useApiErrorHandler({
    error: cloneSARError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to clone SAR.');
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { data: users, error: listUsersError } = useListUsersQuery();

  useRTKErrorHandler({
    error: listUsersError,
    onError: () => {
      return {
        message: 'Failed to load users.',
        level: MessageType.Failure,
      };
    },
  });

  const { error: validateSARError, callback: handleValidateSAR } = useApiCallback(async () => {
    await props.validateSAR(sarToken);
  }, [props.validateSAR, sarToken]);

  useApiErrorHandler({
    error: validateSARError,
    onError: () =>
      props.showMessage(
        'Something went wrong while running validations. Please try again.',
        MessageType.Failure,
      ),
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { error: printSARError, callback: handlePrintSAR } = useApiCallback(async () => {
    await SessionClientDashboard.postResourceAction<SAR>(
      {
        type: ApiModel.SAR,
        id: sarToken,
      },
      'print',
    );

    props.showMessage('You will receive an email when your PDF is ready.', MessageType.Notice);
  }, [sarToken]);

  useApiErrorHandler({
    error: printSARError,
    onError: (error) => {
      if (error instanceof ApiResponseError && error.status === 403) {
        // [chris] todo: isPermissionsError never returns true for the export endpoint.
        // We manually generate the permissions error message.
        props.showMessage(
          "You don't have permissions for an action on this page: Export SAR",
          MessageType.Failure,
        );
      } else {
        props.showMessage('Failed to print SAR, please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { error: exportFincenPDFError, callback: handleExportFincenPDF } =
    useApiCallback(async () => {
      await SessionClientDashboard.postResourceAction<SAR>(
        {
          type: ApiModel.SAR,
          id: sarToken,
        },
        'export-fincen-pdf',
      );

      props.showMessage('You will receive an email when your PDF is ready.', MessageType.Notice);
    }, [sarToken]);

  useApiErrorHandler({
    error: exportFincenPDFError,
    onError: (error) => {
      if (error instanceof ApiResponseError && error.status === 403) {
        // [chris] todo: isPermissionsError never returns true for the export endpoint.
        // We manually generate the permissions error message.
        props.showMessage(
          "You don't have permissions for an action on this page: Export SAR",
          MessageType.Failure,
        );
      } else {
        props.showMessage('Failed to export FinCEN PDF, please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  return (
    <DashboardSARShowView
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      sar={props.sar}
      additionalValidationErrors={props.validationErrors[sarToken]}
      errorMessages={props.errorMessages[sarToken]}
      users={users || []}
      relatedCases={props.relatedCases}
      isLoading={isLoading || loadingSARConfiguration}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      nullStateMessage={nullStateMessages[errorCode.current]}
      currentEnvironment={props.currentEnvironment}
      sarConfiguration={props.sarConfiguration} // SAR configuration may not load if user does not have permissions
      onSARDataUpdate={handleUpdateSAR}
      handleArchiveSAR={handleArchiveSAR}
      handleAutofillSAR={handleAutofillSAR}
      handleCloneSAR={handleCloneSAR}
      handleExportSAR={handleExportSAR}
      handleFileSAR={handleFileSAR}
      handlePrintSAR={handlePrintSAR}
      handleValidateSAR={handleValidateSAR}
      handleExportFincenPDF={handleExportFincenPDF}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  sar: state.sar.sar,
  validationErrors: state.sar.validationErrors,
  errorMessages: state.sar.errorMessages,
  relatedCases: state.sar.cases,
  currentEnvironment: state.currentEnvironment.currentEnvironment,
  sarConfiguration: state.sarConfiguration.sarConfiguration,
  organization: state.organization.organization,
});

const mapDispatchToProps = {
  archiveSAR,
  autofillSAR,
  exportSAR,
  fileSAR,
  loadSAR,
  loadSARConfiguration,
  showMessage,
  updateSAR,
  validateSAR,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardSARShowRoute);
